import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { fromSharedActions } from './shared.actions';
import { sharedQuery } from './shared.selectors';
import { ComponentType } from '@angular/cdk/portal';
import { PageComponent } from '@icc/shared/helpers';
import { EventBusService } from '@icc/shared';
import { map, tap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import { ConfigurationType } from '@icc/shared/types';
import { PreviewData } from './shared.reducers';

@Injectable()
export class SharedFacade {
    constructor(private store: Store<any>) {}

    config$ = this.store.select(sharedQuery.getConfig);
    messages$ = this.store.select(sharedQuery.getMessages);
    languageAndProject$ = this.store.select(sharedQuery.getLanguageAndProject);
    currency$ = this.store.select(sharedQuery.getCurrency);
    fullscreen$ = this.store.select(sharedQuery.getFullscreen);
    changedToolbarTitle$ = this.store.select(sharedQuery.getChangedToolbarTitle);
    preview$ = this.store.select(sharedQuery.getPreview);
    colorsStepMode$ = this.store.select(sharedQuery.getColorsStepMode);
    systemStepMode$ = this.store.select(sharedQuery.getSystemStepMode);
    lightsGlazingStepMode$ = this.store.select(sharedQuery.getLightsGlazingStepMode);
    options$ = this.store.select(sharedQuery.getOptions);
    savingButton$ = this.store.select(sharedQuery.getSavingButton);
    returnButton$ = this.store.select(sharedQuery.getReturnButton);
    additionalReturnButtons$ = this.store.select(sharedQuery.getAdditionalReturnButtons);
    currencies$ = this.store.select(sharedQuery.getCurrencies);

    activeSearch$ = this.store.select(sharedQuery.getActiveSearch);
    searchValue$ = this.store.select(sharedQuery.getSearchValue);
    isModalConfiguration = false;
    getIsModalConfigurator$ = this.store.select(sharedQuery.getIsModalConfigurator).pipe(
        tap(isModal => {
            this.isModalConfiguration = isModal;
        })
    );

    getStepsDescriptions$ = this.store.select(sharedQuery.getStepsDescriptions);

    previewAnimationDone$ = new Subject<void>();
    configuratorInitialized$ = new BehaviorSubject<boolean>(false);

    focusedShutterId$ = this.store.select(sharedQuery.getFocusedShutter);
    rulesMode$ = this.store.select(sharedQuery.getRulesMode);


    closePage(result?: any) {
        this.store.dispatch(
            new fromSharedActions.ClosePage({
                result,
            })
        );
    }


    closeAllPages() {
        this.store.dispatch(
            new fromSharedActions.CloseAllPages()
        )
    }

    openPage(
        id: string,
        page: ComponentType<PageComponent>,
        data?: { [key: string]: string | Function | Array<string | Function> | Object }
    ) {
        this.store.dispatch(new fromSharedActions.OpenPage({ page, id, data }));
    }
    closeFilterSheet(result?: any) {
        this.store.dispatch(
            new fromSharedActions.CloseFilterSheet({
                result,
            })
        );
    }

    changeToolbarTitle(title: string) {
        if (title) {
            this.store.dispatch(
                new fromSharedActions.ChangeToolbarTitle(title)
            )
        }
    }
    openFilterSheet(
        id: string,
        page: ComponentType<PageComponent>,
        data?: { [key: string]: string | Function | Array<string | Function> | Object }
    ) {
        this.store.dispatch(new fromSharedActions.OpenFilterSheet({ page, id, data }));
    }

    togglePreview(data: PreviewData){
        this.store.dispatch(new fromSharedActions.TogglePreview({
            isPreviewEnabled: data.isPreviewEnabled,
            drawData: data.drawData,
        }));
    }

    enableFullscreen() {
        this.store.dispatch(new fromSharedActions.EnableFullscreen());
    }

    disableFullscreen() {
        this.store.dispatch(new fromSharedActions.DisableFullscreen());
    }

    changeColorsStepMode(mode: 'simple' | 'advanced' | 'door_extended') {
        this.store.dispatch(new fromSharedActions.ChangedColorsStepMode(mode));
    }

    changeSystemStepMode(mode: 'list' | 'details') {
        this.store.dispatch(new fromSharedActions.ChangedSystemStepMode(mode));
    }

    changeLightsGlazingStepMode(mode: 'list' | 'extra') {
        this.store.dispatch(new fromSharedActions.ChangedLightsGlazingStepMode(mode));
    }

    enableSearch() {
        this.store.dispatch(new fromSharedActions.EnableSearch());
    }

    disableSearch() {
        this.store.dispatch(new fromSharedActions.DisableSearch());
    }

    stopSearch() {
        this.store.dispatch(new fromSharedActions.StopSearch());
    }

    updateSearch(value: string) {
        this.store.dispatch(new fromSharedActions.UpdateSearch(value));
    }

    closeModalConfigurator(result?: any) {
        this.store.dispatch(
            new fromSharedActions.CloseModalConfigurator({
                result,
            })
        );
    }

    openModalConfigurator(
        id: string,
        configuratorType: ConfigurationType,
        data?: { [key: string]: string | Function | Array<string | Function> | Object }
    ) {
        this.store.dispatch(new fromSharedActions.OpenModalConfigurator({ configuratorType, id, data }));
    }

    focusShutter(shutterId: number | null) {
        this.store.dispatch(new fromSharedActions.FocusShutter(shutterId));
    }
}
